import { useState, useId } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Checkbox, Input } from '../atoms';
import { useToast } from './Toast';

/**
 * AssessorContactCard — an assessor's phones, emails and addresses (spec 0013).
 *
 * Backed by /api/assessors/:id/contact-points. Each kind lists any number of entries, each with a
 * type label ("main", "ARB scheduling", "mailing") and at most one primary. The primary of each
 * kind is what the rest of the app reads (the protest letter's address block, the phone on
 * exports): the server mirrors it into the assessor's own columns, so this card invalidates the
 * assessor record after every write.
 */

export type ContactPointKind = 'addresses' | 'phones' | 'emails';

export interface ContactPoint {
  id: string;
  type: string;
  is_primary: boolean;
  number?: string | null;
  email?: string | null;
  addressee?: string | null;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

type ContactPoints = Record<ContactPointKind, ContactPoint[]>;

interface KindConfig {
  title: string;
  noun: string;
  types: string[];
  fields: Array<{ key: keyof ContactPoint; label: string; required?: boolean; width?: string }>;
}

const KINDS: Record<ContactPointKind, KindConfig> = {
  phones: {
    title: 'Phone numbers', noun: 'phone number',
    types: ['main', 'appeals', 'arb scheduling', 'bpp', 'fax'],
    fields: [{ key: 'number', label: 'Number', required: true }],
  },
  emails: {
    title: 'Email addresses', noun: 'email address',
    types: ['main', 'appeals', 'bpp'],
    fields: [{ key: 'email', label: 'Email', required: true }],
  },
  addresses: {
    title: 'Addresses', noun: 'address',
    types: ['mailing', 'physical'],
    fields: [
      { key: 'addressee', label: 'Addressee' },
      { key: 'line1', label: 'Line 1', required: true },
      { key: 'line2', label: 'Line 2' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', width: '5rem' },
      { key: 'postal_code', label: 'Postal code', width: '8rem' },
    ],
  },
};
const ORDER: ContactPointKind[] = ['phones', 'emails', 'addresses'];

const typeLabel = (t: string) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '—');

function formatPoint(kind: ContactPointKind, p: ContactPoint): string {
  if (kind === 'phones') return p.number ?? '';
  if (kind === 'emails') return p.email ?? '';
  const cityLine = [p.city, [p.state, p.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [p.addressee, p.line1, p.line2, cityLine].filter(Boolean).join(' · ');
}

function asPoints(data: unknown): ContactPoints {
  const d = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as Partial<ContactPoints>;
  return { phones: d.phones ?? [], emails: d.emails ?? [], addresses: d.addresses ?? [] };
}

export interface AssessorContactCardProps {
  assessorId: string;
  canEdit: boolean;
  /** Query keys to refresh after a write (the assessor record, whose legacy columns follow the primaries). */
  invalidateKeys?: unknown[][];
}

export function AssessorContactCard({ assessorId, canEdit, invalidateKeys = [] }: AssessorContactCardProps) {
  const queryKey = ['assessor-contact-points', assessorId];
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => api.get<ContactPoints>(`/api/assessors/${assessorId}/contact-points`, signal),
  });
  const points = asPoints(query.data);

  return (
    <section
      data-testid="assessor-contact-card"
      aria-labelledby={`assessor-contact-${assessorId}`}
      style={{
        background: 'var(--color-card)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)', marginBottom: 'var(--sp-4)',
      }}
    >
      <h3 id={`assessor-contact-${assessorId}`} style={{
        margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
      }}>Contact</h3>
      {query.isLoading && <p style={{ color: 'var(--color-ink-3)', margin: 0 }}>Loading contact details…</p>}
      {query.error && <p style={{ color: 'var(--color-status-red)', margin: 0 }}>Could not load contact details: {(query.error as Error).message}</p>}
      {!query.isLoading && !query.error && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))' }}>
          {ORDER.map((kind) => (
            <KindList key={kind} kind={kind} assessorId={assessorId} points={points[kind]} canEdit={canEdit}
              invalidate={[queryKey, ...invalidateKeys]} />
          ))}
        </div>
      )}
    </section>
  );
}

function KindList({ kind, assessorId, points, canEdit, invalidate }: {
  kind: ContactPointKind; assessorId: string; points: ContactPoint[]; canEdit: boolean; invalidate: unknown[][];
}) {
  const cfg = KINDS[kind];
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const base = `/api/assessors/${assessorId}/contact-points/${kind}`;

  const refresh = () => Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: k })));
  const onError = (err: unknown) => toast({
    variant: 'error',
    message: err instanceof ApiClientError ? (err.body as { error?: string } | null)?.error ?? err.message : `Could not save the ${cfg.noun}.`,
  });

  const save = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: Record<string, unknown> }) =>
      id ? api.put(`${base}/${id}`, body) : api.post(base, body),
    onSuccess: async () => { setEditing(null); await refresh(); },
    onError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${base}/${id}`),
    onSuccess: refresh,
    onError,
  });

  return (
    <div data-testid={`contact-kind-${kind}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-2)' }}>
        <h4 style={{ margin: 0, fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{cfg.title}</h4>
        {canEdit && editing !== 'new' && (
          <Button variant="ghost" size="sm" onClick={() => setEditing('new')}>Add {cfg.noun}</Button>
        )}
      </div>
      {points.length === 0 && editing !== 'new' && (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>No {cfg.noun} yet.</p>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--sp-2)' }}>
        {points.map((p) => (
          <li key={p.id} style={{ fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)', paddingBottom: 'var(--sp-2)' }}>
            {editing === p.id ? (
              <PointForm kind={kind} initial={p} saving={save.isPending}
                onCancel={() => setEditing(null)} onSave={(body) => save.mutate({ id: p.id, body })} />
            ) : (
              <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--color-ink-3)', minWidth: '7rem' }}>{typeLabel(p.type)}</span>
                {/* A basis wide enough for a phone number: when the row runs out of room the buttons move
                    to the next line, instead of the number breaking every two characters. */}
                <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', flex: '1 1 9rem', minWidth: '9rem', overflowWrap: 'anywhere' }}>{formatPoint(kind, p)}</span>
                {p.is_primary && <Badge variant="success">Primary</Badge>}
                {canEdit && (
                  <span style={{ display: 'flex', gap: 'var(--sp-1)' }}>
                    {!p.is_primary && (
                      <Button variant="ghost" size="sm" onClick={() => save.mutate({ id: p.id, body: { is_primary: true } })}>Make primary</Button>
                    )}
                    <Button variant="ghost" size="sm" aria-label={`Edit ${typeLabel(p.type)} ${cfg.noun}`} onClick={() => setEditing(p.id)}>Edit</Button>
                    <Button variant="ghost" size="sm" aria-label={`Delete ${typeLabel(p.type)} ${cfg.noun}`}
                      onClick={() => { if (window.confirm(`Delete this ${cfg.noun}?${p.is_primary ? ' It is the primary, so the assessor will have none until you pick another.' : ''}`)) remove.mutate(p.id); }}>
                      Delete
                    </Button>
                  </span>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {editing === 'new' && (
        <PointForm kind={kind} saving={save.isPending} onCancel={() => setEditing(null)}
          onSave={(body) => save.mutate({ id: null, body })} />
      )}
    </div>
  );
}

function PointForm({ kind, initial, saving, onSave, onCancel }: {
  kind: ContactPointKind; initial?: ContactPoint; saving: boolean;
  onSave: (body: Record<string, unknown>) => void; onCancel: () => void;
}) {
  const cfg = KINDS[kind];
  const listId = useId();
  const [draft, setDraft] = useState<Record<string, unknown>>(() => {
    const d: Record<string, unknown> = { type: initial?.type ?? cfg.types[0], is_primary: initial?.is_primary ?? false };
    for (const f of cfg.fields) d[f.key] = initial?.[f.key] ?? '';
    return d;
  });
  const set = (k: string, v: unknown) => setDraft((prev) => ({ ...prev, [k]: v }));
  const missing = cfg.fields.some((f) => f.required && !String(draft[f.key] ?? '').trim()) || !String(draft.type ?? '').trim();

  return (
    <form
      aria-label={`${initial ? 'Edit' : 'Add'} ${cfg.noun}`}
      onSubmit={(e) => { e.preventDefault(); if (!missing) onSave(draft); }}
      style={{ display: 'grid', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}
    >
      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <label style={{ display: 'grid', gap: 'var(--sp-0-5)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', width: '10rem' }}>
          Type
          <Input size="sm" list={listId} value={String(draft.type ?? '')} onChange={(e) => set('type', e.target.value)} />
          <datalist id={listId}>{cfg.types.map((t) => <option key={t} value={t} />)}</datalist>
        </label>
        {cfg.fields.map((f) => (
          <label key={f.key} style={{ display: 'grid', gap: 'var(--sp-0-5)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', width: f.width ?? (kind === 'addresses' ? '12rem' : '14rem') }}>
            {f.label}{f.required ? ' *' : ''}
            <Input size="sm" value={String(draft[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)}
              type={kind === 'emails' ? 'email' : kind === 'phones' ? 'tel' : 'text'} />
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
        <Checkbox checked={!!draft.is_primary} onChange={(e) => set('is_primary', e.target.checked)} label="Primary" />
        <Button type="submit" size="sm" variant="primary" loading={saving} disabled={saving || missing}>Save</Button>
        <Button type="button" size="sm" variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </form>
  );
}
